import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Container,
  Paper,
  Box,
  Typography,
  Button,
  Avatar,
  CircularProgress,
  Alert,
  Grid,
  TextField,
  MenuItem,
  Divider,
  FormControlLabel,
  Switch,
  IconButton,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SaveIcon from "@mui/icons-material/Save";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";

import patientService from "../../services/patientService";

const PatientCreatePage = () => {
  const navigate = useNavigate();

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Single state object for all patient data - EMPTY for new patient
  const [formData, setFormData] = useState({
    // Demographics
    firstName: "",
    middleName: "",
    lastName: "",
    email: "",
    phone: "",
    dateOfBirth: "",
    gender: "",
    address: "",
    city: "",
    state: "",
    zip: "",
    country: "United States",

    // Medical Info
    ckdStage: "",
    diagnosisDate: "",
    dialysisModality: "",
    notes: "",

    // Medications
    medications: [],

    // Allergies
    allergies: [],

    // Medical History
    pastDiagnoses: [],

    // Vitals
    vitals: [],

    // Consents
    hipaaConsent: false,
    termsAccepted: false,
    privacyPolicyAccepted: false,
    researchConsent: false,
    dataSharingConsent: false,
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  // Medication handlers
  const handleAddMedication = () => {
    setFormData((prev) => ({
      ...prev,
      medications: [
        ...prev.medications,
        { name: "", dosage: "", frequency: "", startDate: "", notes: "" },
      ],
    }));
  };

  const handleRemoveMedication = (index) => {
    setFormData((prev) => ({
      ...prev,
      medications: prev.medications.filter((_, i) => i !== index),
    }));
  };

  const handleMedicationChange = (index, field, value) => {
    setFormData((prev) => ({
      ...prev,
      medications: prev.medications.map((med, i) =>
        i === index ? { ...med, [field]: value } : med
      ),
    }));
  };

  // Allergy handlers
  const handleAddAllergy = () => {
    setFormData((prev) => ({
      ...prev,
      allergies: [
        ...prev.allergies,
        { allergen: "", reaction: "", severity: "", notes: "" },
      ],
    }));
  };

  const handleRemoveAllergy = (index) => {
    setFormData((prev) => ({
      ...prev,
      allergies: prev.allergies.filter((_, i) => i !== index),
    }));
  };

  const handleAllergyChange = (index, field, value) => {
    setFormData((prev) => ({
      ...prev,
      allergies: prev.allergies.map((allergy, i) =>
        i === index ? { ...allergy, [field]: value } : allergy
      ),
    }));
  };

  // Diagnosis handlers
  const handleAddDiagnosis = () => {
    setFormData((prev) => ({
      ...prev,
      pastDiagnoses: [
        ...prev.pastDiagnoses,
        { condition: "", diagnosisDate: "", notes: "" },
      ],
    }));
  };

  const handleRemoveDiagnosis = (index) => {
    setFormData((prev) => ({
      ...prev,
      pastDiagnoses: prev.pastDiagnoses.filter((_, i) => i !== index),
    }));
  };

  const handleDiagnosisChange = (index, field, value) => {
    setFormData((prev) => ({
      ...prev,
      pastDiagnoses: prev.pastDiagnoses.map((diagnosis, i) =>
        i === index ? { ...diagnosis, [field]: value } : diagnosis
      ),
    }));
  };

  // Vitals handlers
  const handleAddVital = () => {
    setFormData((prev) => ({
      ...prev,
      vitals: [
        ...prev.vitals,
        {
          date: new Date().toISOString().split("T")[0],
          bloodPressureSystolic: "",
          bloodPressureDiastolic: "",
          heartRate: "",
          temperature: "",
          weight: "",
          height: "",
          oxygenSaturation: "",
          notes: "",
        },
      ],
    }));
  };

  const handleRemoveVital = (index) => {
    setFormData((prev) => ({
      ...prev,
      vitals: prev.vitals.filter((_, i) => i !== index),
    }));
  };

  const handleVitalChange = (index, field, value) => {
    setFormData((prev) => ({
      ...prev,
      vitals: prev.vitals.map((vital, i) =>
        i === index ? { ...vital, [field]: value } : vital
      ),
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      // Validate required fields
      if (!formData.firstName?.trim()) {
        setError("First Name is required");
        setSaving(false);
        window.scrollTo({ top: 0, behavior: "smooth" });
        return;
      }
      if (!formData.lastName?.trim()) {
        setError("Last Name is required");
        setSaving(false);
        window.scrollTo({ top: 0, behavior: "smooth" });
        return;
      }
      if (!formData.email?.trim()) {
        setError("Email is required");
        setSaving(false);
        window.scrollTo({ top: 0, behavior: "smooth" });
        return;
      }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        setError("Email format is invalid");
        setSaving(false);
        window.scrollTo({ top: 0, behavior: "smooth" });
        return;
      }

      // Validate consents
      if (!formData.hipaaConsent) {
        setError("HIPAA consent is required");
        setSaving(false);
        window.scrollTo({ top: 0, behavior: "smooth" });
        return;
      }
      if (!formData.termsAccepted) {
        setError("Terms & Conditions must be accepted");
        setSaving(false);
        window.scrollTo({ top: 0, behavior: "smooth" });
        return;
      }
      if (!formData.privacyPolicyAccepted) {
        setError("Privacy Policy must be accepted");
        setSaving(false);
        window.scrollTo({ top: 0, behavior: "smooth" });
        return;
      }

      // Prepare data for backend
      const backendData = {
        firstName: formData.firstName.trim(),
        middleName: formData.middleName?.trim() || null,
        lastName: formData.lastName.trim(),
        dateOfBirth: formData.dateOfBirth || null,
        gender: formData.gender || null,
        email: formData.email.trim(),
        phoneNumber: formData.phone?.trim() || null,
        streetAddress: formData.address?.trim() || null,
        city: formData.city?.trim() || null,
        state: formData.state || null,
        zipCode: formData.zip?.trim() || null,
        country: formData.country || "United States",
        ckdStage: formData.ckdStage || null,
        diagnosisDate: formData.diagnosisDate || null,
        dialysisModality: formData.dialysisModality || null,
        notes: formData.notes?.trim() || null,

        // Consents
        hipaaConsent: formData.hipaaConsent,
        termsAccepted: formData.termsAccepted,
        privacyPolicyAccepted: formData.privacyPolicyAccepted,
        researchConsent: formData.researchConsent || false,
        dataSharingConsent: formData.dataSharingConsent || false,

        // Convert medications to strings
        currentMedications: formData.medications
          .map((med) =>
            med.name
              ? `${med.name}${med.dosage ? " - " + med.dosage : ""}${
                  med.frequency ? " - " + med.frequency : ""
                }`
              : ""
          )
          .filter((m) => m),

        // Convert allergies to strings
        allergies: formData.allergies
          .map((allergy) => allergy.allergen || "")
          .filter((a) => a),

        // Convert diagnoses to strings
        medicalConditions: formData.pastDiagnoses
          .map((d) => d.condition || "")
          .filter((c) => c),
      };

      console.log("📤 Creating new patient:", backendData);

      const response = await patientService.createPatient(backendData);

      if (response.success) {
        setSuccess("Patient registered successfully!");
        setTimeout(() => {
          if (response.data?.id) {
            navigate(`/patients/${response.data.id}`);
          } else {
            navigate("/patients");
          }
        }, 1500);
      } else {
        setError(response.message || "Failed to create patient");
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    } catch (err) {
      console.error("❌ Error creating patient:", err);
      setError("Failed to create patient: " + err.message);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: "flex", alignItems: "center", gap: 2 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/patients")}
        >
          Back to Patients
        </Button>
      </Box>

      {/* Patient Header */}
      <Paper
        elevation={3}
        sx={{
          p: 3,
          mb: 3,
          background: "linear-gradient(135deg, #11998e 0%, #38ef7d 100%)",
          color: "white",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, flex: 1 }}>
            <Avatar
              sx={{
                width: 64,
                height: 64,
                bgcolor: "rgba(255,255,255,0.3)",
                fontSize: "1.5rem",
              }}
            >
              P
            </Avatar>
            <Box>
              <Typography variant="h4" fontWeight="bold">
                New Patient Registration
              </Typography>
              <Typography variant="subtitle1" sx={{ opacity: 0.9 }}>
                Complete patient information for clinical trial matching
              </Typography>
            </Box>
          </Box>
          <Button
            variant="contained"
            size="large"
            startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
            onClick={handleSave}
            disabled={saving}
            sx={{
              bgcolor: "white",
              color: "success.main",
              fontWeight: "bold",
              "&:hover": { bgcolor: "rgba(255,255,255,0.9)" },
            }}
          >
            {saving ? "Saving..." : "Register Patient"}
          </Button>
        </Box>
      </Paper>

      {/* Messages */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert
          severity="success"
          sx={{ mb: 3 }}
          onClose={() => setSuccess(null)}
        >
          {success}
        </Alert>
      )}

      {/* Main Form */}
      <Paper elevation={2} sx={{ p: 4 }}>
        {/* DEMOGRAPHICS SECTION */}
        <Typography variant="h5" color="primary" gutterBottom sx={{ mb: 3 }}>
          Demographics
        </Typography>
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              required
              label="First Name"
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Middle Name"
              name="middleName"
              value={formData.middleName}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              required
              label="Last Name"
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              required
              label="Email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Date of Birth"
              name="dateOfBirth"
              type="date"
              value={formData.dateOfBirth}
              onChange={handleChange}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              select
              label="Gender"
              name="gender"
              value={formData.gender}
              onChange={handleChange}
            >
              <MenuItem value="">Select Gender</MenuItem>
              <MenuItem value="Male">Male</MenuItem>
              <MenuItem value="Female">Female</MenuItem>
              <MenuItem value="Other">Other</MenuItem>
              <MenuItem value="Prefer not to say">Prefer not to say</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Address"
              name="address"
              value={formData.address}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12} md={5}>
            <TextField
              fullWidth
              label="City"
              name="city"
              value={formData.city}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="State"
              name="state"
              value={formData.state}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              fullWidth
              label="ZIP Code"
              name="zip"
              value={formData.zip}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              fullWidth
              label="Country"
              name="country"
              value={formData.country}
              onChange={handleChange}
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 4 }} />

        {/* MEDICAL INFORMATION SECTION */}
        <Typography variant="h5" color="primary" gutterBottom sx={{ mb: 3 }}>
          Medical Information
        </Typography>
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              select
              label="CKD Stage"
              name="ckdStage"
              value={formData.ckdStage}
              onChange={handleChange}
            >
              <MenuItem value="">Select Stage</MenuItem>
              <MenuItem value="1">Stage 1</MenuItem>
              <MenuItem value="2">Stage 2</MenuItem>
              <MenuItem value="3a">Stage 3a</MenuItem>
              <MenuItem value="3b">Stage 3b</MenuItem>
              <MenuItem value="4">Stage 4</MenuItem>
              <MenuItem value="5">Stage 5</MenuItem>
              <MenuItem value="5D">Stage 5D (Dialysis-dependent)</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Diagnosis Date"
              name="diagnosisDate"
              type="date"
              value={formData.diagnosisDate}
              onChange={handleChange}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              select
              label="Dialysis Modality"
              name="dialysisModality"
              value={formData.dialysisModality}
              onChange={handleChange}
            >
              <MenuItem value="">Select Modality</MenuItem>
              <MenuItem value="Pre-dialysis">Pre-dialysis</MenuItem>
              <MenuItem value="Hemodialysis">Hemodialysis</MenuItem>
              <MenuItem value="Peritoneal Dialysis">Peritoneal Dialysis</MenuItem>
              <MenuItem value="Transplant">Transplant</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Notes"
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              multiline
              rows={4}
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 4 }} />

        {/* MEDICATIONS SECTION */}
        <Box sx={{ mb: 4 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 3,
            }}
          >
            <Typography variant="h5" color="primary">
              Current Medications
            </Typography>
            <Button
              startIcon={<AddIcon />}
              onClick={handleAddMedication}
              variant="contained"
            >
              Add Medication
            </Button>
          </Box>

          {formData.medications && formData.medications.length > 0 ? (
            formData.medications.map((med, index) => (
              <Box
                key={index}
                sx={{
                  mb: 3,
                  p: 2,
                  border: "1px solid #ddd",
                  borderRadius: 1,
                  bgcolor: "#f9f9f9",
                }}
              >
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Medication Name"
                      value={med.name || ""}
                      onChange={(e) =>
                        handleMedicationChange(index, "name", e.target.value)
                      }
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      label="Dosage"
                      value={med.dosage || ""}
                      onChange={(e) =>
                        handleMedicationChange(index, "dosage", e.target.value)
                      }
                      placeholder="e.g., 10mg"
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      label="Frequency"
                      value={med.frequency || ""}
                      onChange={(e) =>
                        handleMedicationChange(
                          index,
                          "frequency",
                          e.target.value
                        )
                      }
                      placeholder="e.g., Twice daily"
                    />
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    md={2}
                    sx={{ display: "flex", alignItems: "center" }}
                  >
                    <IconButton
                      color="error"
                      onClick={() => handleRemoveMedication(index)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Grid>
                </Grid>
              </Box>
            ))
          ) : (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontStyle: "italic", mb: 2 }}
            >
              No medications added yet. Click "Add Medication" to add one.
            </Typography>
          )}
        </Box>

        <Divider sx={{ my: 4 }} />

        {/* ALLERGIES SECTION */}
        <Box sx={{ mb: 4 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 3,
            }}
          >
            <Typography variant="h5" color="primary">
              Allergies
            </Typography>
            <Button
              startIcon={<AddIcon />}
              onClick={handleAddAllergy}
              variant="contained"
              color="warning"
            >
              Add Allergy
            </Button>
          </Box>

          {formData.allergies && formData.allergies.length > 0 ? (
            formData.allergies.map((allergy, index) => (
              <Box
                key={index}
                sx={{
                  mb: 3,
                  p: 2,
                  border: "1px solid #ddd",
                  borderRadius: 1,
                  bgcolor: "#fff9e6",
                }}
              >
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Allergen"
                      value={allergy.allergen || ""}
                      onChange={(e) =>
                        handleAllergyChange(index, "allergen", e.target.value)
                      }
                      placeholder="e.g., Penicillin"
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      label="Reaction"
                      value={allergy.reaction || ""}
                      onChange={(e) =>
                        handleAllergyChange(index, "reaction", e.target.value)
                      }
                      placeholder="e.g., Rash"
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      select
                      label="Severity"
                      value={allergy.severity || ""}
                      onChange={(e) =>
                        handleAllergyChange(index, "severity", e.target.value)
                      }
                    >
                      <MenuItem value="">Select</MenuItem>
                      <MenuItem value="Mild">Mild</MenuItem>
                      <MenuItem value="Moderate">Moderate</MenuItem>
                      <MenuItem value="Severe">Severe</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    md={2}
                    sx={{ display: "flex", alignItems: "center" }}
                  >
                    <IconButton
                      color="error"
                      onClick={() => handleRemoveAllergy(index)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Grid>
                </Grid>
              </Box>
            ))
          ) : (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontStyle: "italic", mb: 2 }}
            >
              No allergies recorded yet. Click "Add Allergy" to add one.
            </Typography>
          )}
        </Box>

        <Divider sx={{ my: 4 }} />

        {/* MEDICAL HISTORY SECTION */}
        <Box sx={{ mb: 4 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 3,
            }}
          >
            <Typography variant="h5" color="primary">
              Past Diagnoses
            </Typography>
            <Button
              startIcon={<AddIcon />}
              onClick={handleAddDiagnosis}
              variant="outlined"
            >
              Add Diagnosis
            </Button>
          </Box>

          {formData.pastDiagnoses && formData.pastDiagnoses.length > 0 ? (
            formData.pastDiagnoses.map((diagnosis, index) => (
              <Box
                key={index}
                sx={{ mb: 3, p: 2, border: "1px solid #ddd", borderRadius: 1 }}
              >
                <Grid container spacing={2}>
                  <Grid item xs={12} md={5}>
                    <TextField
                      fullWidth
                      label="Condition"
                      value={diagnosis.condition || ""}
                      onChange={(e) =>
                        handleDiagnosisChange(
                          index,
                          "condition",
                          e.target.value
                        )
                      }
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      label="Diagnosis Date"
                      type="date"
                      value={diagnosis.diagnosisDate || ""}
                      onChange={(e) =>
                        handleDiagnosisChange(
                          index,
                          "diagnosisDate",
                          e.target.value
                        )
                      }
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      label="Notes"
                      value={diagnosis.notes || ""}
                      onChange={(e) =>
                        handleDiagnosisChange(index, "notes", e.target.value)
                      }
                    />
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    md={1}
                    sx={{ display: "flex", alignItems: "center" }}
                  >
                    <IconButton
                      color="error"
                      onClick={() => handleRemoveDiagnosis(index)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Grid>
                </Grid>
              </Box>
            ))
          ) : (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontStyle: "italic", mb: 2 }}
            >
              No past diagnoses recorded yet.
            </Typography>
          )}
        </Box>

        <Divider sx={{ my: 4 }} />

        {/* CONSENTS SECTION */}
        <Typography
          variant="h5"
          color="warning.main"
          gutterBottom
          sx={{ mb: 3 }}
        >
          Consent & Privacy (Required)
        </Typography>
        <Grid container spacing={2} sx={{ mb: 4 }}>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.hipaaConsent}
                  onChange={handleChange}
                  name="hipaaConsent"
                  color="primary"
                />
              }
              label="HIPAA Consent (Required) - I authorize the use and disclosure of my health information"
            />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.termsAccepted}
                  onChange={handleChange}
                  name="termsAccepted"
                  color="primary"
                />
              }
              label="Terms & Conditions (Required) - I agree to the terms and conditions"
            />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.privacyPolicyAccepted}
                  onChange={handleChange}
                  name="privacyPolicyAccepted"
                  color="primary"
                />
              }
              label="Privacy Policy (Required) - I have read and accept the privacy policy"
            />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.researchConsent}
                  onChange={handleChange}
                  name="researchConsent"
                  color="primary"
                />
              }
              label="Research Consent (Optional) - I consent to participate in research studies"
            />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.dataSharingConsent}
                  onChange={handleChange}
                  name="dataSharingConsent"
                  color="primary"
                />
              }
              label="Data Sharing Consent (Optional) - I consent to sharing my data with research partners"
            />
          </Grid>
        </Grid>

        {(!formData.hipaaConsent ||
          !formData.termsAccepted ||
          !formData.privacyPolicyAccepted) && (
          <Alert severity="warning" sx={{ mb: 4 }}>
            Please accept all required consents (HIPAA, Terms & Conditions,
            Privacy Policy) before registering.
          </Alert>
        )}
      </Paper>
    </Container>
  );
};

export default PatientCreatePage;
